import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SaveConfirmDialog } from "@/components/ui/confirm-dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  User,
  Phone,
  CreditCard,
  Building2,
  Wallet,
  TrendingUp,
  Gift,
  Users,
  History,
  RefreshCw,
  Save,
  ArrowUpCircle,
  ArrowDownCircle,
  Loader2,
  Shield,
  Clock,
  Target,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  LayoutDashboard,
  Settings,
  Banknote,
  PiggyBank,
  Gamepad2,
  Receipt,
  UserCog,
  Copy,
  Power,
  PowerOff,
  CalendarIcon,
  Check,
  Trash2,
  UserPlus,
} from "lucide-react";
import { Member } from "@/domain/user/user.types";
import { getUserService } from "@/domain/user/user.service";
import { TransactionService } from "@/domain/transaction";
import { getAuditLogService } from "@/domain/audit-log";
import {
  AuditLog,
  AuditActionLabels,
} from "@/domain/audit-log/audit-log.types";
import {
  callCloudFunction,
  queryClass,
} from "@/services/adapters/parse/parse-client";
import { cn } from "@/lib/utils";
import { useCurrency } from "@/stores/currency-store";
import { useAuth } from "@/components/auth";
import {
  sanitizeName,
  sanitizePhone,
  sanitizeBankAccount,
} from "@/lib/sanitize";
import { BankLogo } from "@/components/ui/bank-logo";
import { getActiveBanks, findBank, type CurrencyCode } from "@/data/banks";
import { toast } from "sonner";
import { TransactionTable } from "@/features/transactions/components/transaction-table";
import { MemberBetHistoryTab } from "@/features/members/components/member-bet-history-tab";
import { CommissionV2Service, type CommissionWithdrawV2 } from "@/domain/commission-v2";
import type { Transaction } from "@/domain/transaction/transaction.types";
import { TransactionDialog } from "@/features/transactions/components/transaction-dialog";

interface MemberDetailDialogProps {
  member: Member | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

// 🔐 Permission types สำหรับอนาคต
type MemberAction =
  | "updateMemberInfo"
  | "activeMember"
  | "suspendMember"
  | "resetTurnOverCash"
  | "resetPromotion"
  | "resetPerDayCondition"
  | "clearCredit";

// Confirmation Dialog State
interface ActionConfirm {
  open: boolean;
  action: MemberAction | null;
  title: string;
  description: string;
  variant: "default" | "danger";
}

// Removed DepositWithdraw interface - using Transaction type instead

export function MemberDetailDialog({
  member: initialMember,
  open,
  onOpenChange,
  onSaved,
}: MemberDetailDialogProps) {
  const { format: formatCurrency, currency } = useCurrency();
  const { sessionToken } = useAuth();

  // 🔄 Live Member Data - แยกจาก prop เพื่อ update ได้ realtime
  const [memberData, setMemberData] = useState<Member | null>(null);
  const member = memberData || initialMember; // ใช้ memberData ถ้ามี, fallback เป็น initialMember

  // ดึง bank list ตาม currency ที่เลือก
  const BANK_LIST = getActiveBanks(currency.code as CurrencyCode).map((b) => ({
    id: b.id,
    name: b.name,
    code: b.code.toUpperCase(),
  }));

  const [activeTab, setActiveTab] = useState("overview");
  const [, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmSave, setShowConfirmSave] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    lineId: "",
    bankName: "",
    bankAccount: "",
    password: "",
  });

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [memberRefer, setMemberRefer] = useState<{
    level1Count: number;
    level2Count: number;
    level1Income: number;
    level2Income: number;
    totalAmount: number;
    totalEarned: number;
    totalWithdrawn: number;
    withdrawMin: number;
    withdrawAvailableFrom: string;
    canWithdraw: boolean;
    withdrawTimeMessage: string;
    withdrawHistory: CommissionWithdrawV2[];
    lastCalculatedAt?: string;
    childList?: { id: string; username: string; level: number }[];
  } | null>(null);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);

  // Transactions Pagination
  const [txPage, setTxPage] = useState(1);
  const [txLimit, setTxLimit] = useState(10);
  const [txTotal, setTxTotal] = useState(0);
  const [isLoadingTx, setIsLoadingTx] = useState(false);

  // Transaction Dialog State
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);

  // 📋 Audit Logs State
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLimit] = useState(10);
  const [auditTotal, setAuditTotal] = useState(0);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null); // 🆕 สำหรับ expand log

  // 💰 Finance Tab State - สรุปรายวัน
  interface DailyTransaction {
    id: string;
    type: "Deposit" | "Withdraw";
    amount: number;
    dateTime: string;
    channel: string;
    status: string;
    promotionCode?: string;
    isBonus?: boolean;
  }
  const [selectedFinanceDate, setSelectedFinanceDate] = useState<Date>(
    new Date()
  );
  const [dailyTransactions, setDailyTransactions] = useState<
    DailyTransaction[]
  >([]);
  const [dailySummary, setDailySummary] = useState({
    totalDeposit: 0,
    totalWithdraw: 0,
    totalBonus: 0,
    netProfit: 0,
    transactionCount: 0,
  });
  const [isLoadingDailyFinance, setIsLoadingDailyFinance] = useState(false);

  // 🎁 Promotion Tab State
  const [activePromotions, setActivePromotions] = useState<any[]>([]);
  const [memberNotifications, setMemberNotifications] = useState<any[]>([]);
  const [isLoadingPromotions, setIsLoadingPromotions] = useState(false);
  const [isSendingPromotion, setIsSendingPromotion] = useState<string | null>(
    null
  );
  const [isDeletingNotification, setIsDeletingNotification] = useState<
    string | null
  >(null);
  const [promoHistoryLimit, setPromoHistoryLimit] = useState(10);
  const [promoConfirmDialog, setPromoConfirmDialog] = useState<{
    open: boolean;
    type: "send" | "delete" | null;
    promoId: string | null;
    promoName: string;
    notifId: string | null;
  }>({
    open: false,
    type: null,
    promoId: null,
    promoName: "",
    notifId: null,
  });

  // 🎯 Auto Update & Actions State
  const [isUpdatingInfo, setIsUpdatingInfo] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<MemberAction | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ActionConfirm>({
    open: false,
    action: null,
    title: "",
    description: "",
    variant: "default",
  });
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // 🔐 Permission check (placeholder สำหรับอนาคต)
  const canPerformAction = useCallback((_action: MemberAction): boolean => {
    // TODO: ใส่ logic ตรวจสอบ permission จริงที่นี่
    // เช่น: return user.permissions?.includes(_action)
    return true; // อนุญาตทั้งหมดสำหรับตอนนี้
  }, []);

  useEffect(() => {
    if (initialMember && open) {
      // Reset memberData เมื่อเปิด dialog ใหม่
      setMemberData(null);

      // 🆕 Reset audit logs เพื่อไม่ให้แสดงข้อมูลของคนก่อนหน้า
      setAuditLogs([]);
      setAuditTotal(0);
      setAuditPage(1);

      // 🆕 Reset finance tab data
      setSelectedFinanceDate(new Date());
      setDailyTransactions([]);
      setDailySummary({
        totalDeposit: 0,
        totalWithdraw: 0,
        totalBonus: 0,
        netProfit: 0,
        transactionCount: 0,
      });

      // 🆕 Reset transactions tab data
      setTransactions([]);
      setTxPage(1);
      setTxTotal(0);

      // 🆕 Reset promotion tab data
      setActivePromotions([]);
      setMemberNotifications([]);

      setFormData({
        firstName: initialMember.firstName || "",
        lastName: initialMember.lastName || "",
        phone: initialMember.phone || "",
        email: initialMember.email || "",
        lineId: initialMember.lineId || "",
        bankName: initialMember.bankName || "",
        bankAccount: initialMember.bankAccount || "",
        password: "",
      });
      setActiveTab("overview");
      setExpandedLogId(null);

      // 🔄 Auto Update Member Info ทุกครั้งที่เปิด dialog
      autoUpdateMemberInfo(initialMember.id);
    }
  }, [initialMember, open]);

  // 🔄 Auto เรียก updateMemberInfo เมื่อเปิด dialog เพื่อ sync ข้อมูลล่าสุด
  const autoUpdateMemberInfo = async (memberId: string) => {
    setIsUpdatingInfo(true);
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) return;

      console.log("🔄 [MemberDetail] Auto updating member info...");
      await callCloudFunction("updateMemberInfo", { id: memberId }, token);

      const now = new Date();
      setLastUpdateTime(
        now.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })
      );

      // 🔄 ดึงข้อมูล member ใหม่หลัง update
      await refreshMemberData(memberId);

      // Load member details หลัง update
      await loadMemberDetails(memberId);

      console.log("✅ [MemberDetail] Member info updated");
    } catch (error) {
      console.error("❌ [MemberDetail] Auto update failed:", error);
      // ยังคง load ข้อมูลแม้ update ล้มเหลว
      await loadMemberDetails(memberId);
    } finally {
      setIsUpdatingInfo(false);
    }
  };

  // 🔄 ดึงข้อมูล member ใหม่จาก server
  const refreshMemberData = async (memberId: string) => {
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) return;

      console.log("🔄 [MemberDetail] Refreshing member data...");
      const userService = await getUserService();
      const updatedMember = await userService.getMember(token, memberId);

      if (updatedMember) {
        setMemberData(updatedMember);

        // Update form data ด้วย
        setFormData({
          firstName: updatedMember.firstName || "",
          lastName: updatedMember.lastName || "",
          phone: updatedMember.phone || "",
          email: updatedMember.email || "",
          lineId: updatedMember.lineId || "",
          bankName: updatedMember.bankName || "",
          bankAccount: updatedMember.bankAccount || "",
          password: "",
        });

        console.log(
          "✅ [MemberDetail] Member data refreshed:",
          updatedMember.status,
          updatedMember.balance
        );
      }
    } catch (error) {
      console.error("❌ [MemberDetail] Failed to refresh member data:", error);
    }
  };

  // 🔗 ดึงข้อมูล Commission V2 (แนะนำเพื่อน) จาก Backend
  const loadMemberReferral = async (memberId: string) => {
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");

      if (!token) {
        console.error("No session token for loading referral");
        return;
      }

      console.log("📡 [MemberDetail] Loading Commission V2 data:", memberId);
      
      // ใช้ Commission V2 Service
      const result = await CommissionV2Service.getMemberCommission(memberId, token);

      if (result.success && result.data) {
        const data = result.data;
        
        // ดึงรายชื่อเพื่อนที่แนะนำมา (ถ้ามี)
        let childList: { id: string; username: string; level: number }[] = [];
        try {
          const referResult = await callCloudFunction(
            "getMemberReferChildList",
            { member_id: memberId },
            token
          );
          if (referResult?.children) {
            childList = referResult.children;
          }
        } catch (err) {
          console.log("📌 No child list available (optional)");
        }
        
        setMemberRefer({
          level1Count: data.member_child_count || 0,
          level2Count: data.member_child_count2 || 0,
          level1Income: data.l1_amount || 0,
          level2Income: data.l2_amount || 0,
          totalAmount: data.total_amount || 0,
          totalEarned: data.total_earned || 0,
          totalWithdrawn: data.total_withdrawn || 0,
          withdrawMin: data.withdraw_min || 100,
          withdrawAvailableFrom: data.withdraw_available_from || "06:00",
          canWithdraw: data.can_withdraw || false,
          withdrawTimeMessage: data.withdraw_time_message || "",
          withdrawHistory: data.withdraw_history || [],
          lastCalculatedAt: data.last_calculated_at,
          childList,
        });
        console.log("✅ [MemberDetail] Commission V2 loaded:", result.data);
      } else {
        // ไม่มีข้อมูล Commission V2
        setMemberRefer({
          level1Count: 0,
          level2Count: 0,
          level1Income: 0,
          level2Income: 0,
          totalAmount: 0,
          totalEarned: 0,
          totalWithdrawn: 0,
          withdrawMin: 100,
          withdrawAvailableFrom: "06:00",
          canWithdraw: false,
          withdrawTimeMessage: "ยังไม่มีข้อมูล Commission",
          withdrawHistory: [],
          childList: [],
        });
      }
    } catch (error) {
      console.error("❌ [MemberDetail] Error loading Commission V2:", error);
      setMemberRefer({
        level1Count: 0,
        level2Count: 0,
        level1Income: 0,
        level2Income: 0,
        totalAmount: 0,
        totalEarned: 0,
        totalWithdrawn: 0,
        withdrawMin: 100,
        withdrawAvailableFrom: "06:00",
        canWithdraw: false,
        withdrawTimeMessage: "เกิดข้อผิดพลาด",
        withdrawHistory: [],
        childList: [],
      });
    }
  };

  // 💰 ถอน Commission (ถอนทั้งหมด)
  const handleWithdrawCommission = async () => {
    if (!member || !memberRefer) return;
    
    const amount = memberRefer.totalAmount;
    
    // ตรวจสอบว่ายอดถึงขั้นต่ำหรือยัง
    if (amount < memberRefer.withdrawMin) {
      toast.error(`ยอดยังไม่ถึงขั้นต่ำ (ขั้นต่ำ ${memberRefer.withdrawMin} บาท)`);
      return;
    }
    
    setIsWithdrawing(true);
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
        
      // ถอนทั้งหมด - ไม่ต้องส่ง amount
      const result = await CommissionV2Service.withdrawCommission(
        member.id,
        token || undefined
      );
      
      if (result.success) {
        toast.success(`ถอน Commission สำเร็จ ${amount.toFixed(2)} บาท`);
        setShowWithdrawDialog(false);
        // Reload data
        await loadMemberReferral(member.id);
      } else {
        toast.error(result.message || "ไม่สามารถถอนได้");
      }
    } catch (error: any) {
      console.error("Withdraw error:", error);
      toast.error(error.message || "เกิดข้อผิดพลาดในการถอน");
    } finally {
      setIsWithdrawing(false);
    }
  };

  const loadMemberDetails = async (memberId: string) => {
    setIsLoading(true);
    try {
      // ดึงข้อมูล Referral จาก Backend
      await loadMemberReferral(memberId);

      // Load transactions แยก
      await loadMemberTransactions(memberId, 1, txLimit);

      // 🎁 โหลด Promotions ตั้งแต่เปิด dialog
      await loadPromotionData(memberId);

      // 💰 โหลด Daily Finance สำหรับวันนี้ตั้งแต่เปิด dialog
      await loadDailyFinanceSummary(memberId, new Date());
    } catch (error) {
      console.error("❌ [MemberDetail] Error loading details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 🔗 ดึงข้อมูลธุรกรรมจริงของ Member คนนี้ (with pagination) - ใช้ Transaction type
  const loadMemberTransactions = async (
    memberId: string,
    page: number,
    limit: number
  ) => {
    setIsLoadingTx(true);
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");

      if (!token) {
        console.error("No session token for loading transactions");
        return;
      }

      console.log("📡 [MemberDetail] Loading transactions:", {
        memberId,
        page,
        limit,
      });

      const result = await TransactionService.findByMemberId(token, memberId, {
        limit,
        page,
      });

      // ✅ ใช้ Transaction type โดยตรง (ไม่ต้อง map)
      console.log(
        "✅ [MemberDetail] Loaded",
        result.items.length,
        "of",
        result.total,
        "transactions"
      );
      setTransactions(result.items);
      setTxTotal(result.total);
      setTxPage(page);
    } catch (error) {
      console.error("❌ [MemberDetail] Error loading transactions:", error);
      setTransactions([]);
      setTxTotal(0);
    } finally {
      setIsLoadingTx(false);
    }
  };

  // ✅ Handlers สำหรับ TransactionTable actions
  const handleViewTransactionDetail = (tx: Transaction) => {
    setSelectedTransaction(tx);
    setTransactionDialogOpen(true);
  };

  const handleViewTransactionMember = (_memberId: string) => {
    // ไม่ต้องทำอะไร เพราะเราอยู่ใน member dialog อยู่แล้ว
    // memberId ควรจะตรงกับ member.id ที่เปิด dialog อยู่
  };

  const handleApproveManual = async (id: string) => {
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error("กรุณาเข้าสู่ระบบใหม่");
        return;
      }

      await TransactionService.approveManualV2(token, id);
      toast.success("อนุมัติรายการสำเร็จ");

      // Reload transactions
      if (member) {
        await loadMemberTransactions(member.id, txPage, txLimit);
      }
    } catch (error: any) {
      console.error("Error approving transaction:", error);
      toast.error(error.message || "ไม่สามารถอนุมัติรายการได้");
    }
  };

  const handleApproveAuto = async (id: string) => {
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error("กรุณาเข้าสู่ระบบใหม่");
        return;
      }

      await TransactionService.approveAutoV2(token, id);
      toast.success("อนุมัติรายการอัตโนมัติสำเร็จ");

      // Reload transactions
      if (member) {
        await loadMemberTransactions(member.id, txPage, txLimit);
      }
    } catch (error: any) {
      console.error("Error approving transaction:", error);
      toast.error(error.message || "ไม่สามารถอนุมัติรายการได้");
    }
  };

  const handleReject = async (id: string) => {
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error("กรุณาเข้าสู่ระบบใหม่");
        return;
      }

      await TransactionService.rejectV2(token, id, "ปฏิเสธโดย Admin");
      toast.success("ปฏิเสธรายการสำเร็จ");

      // Reload transactions
      if (member) {
        await loadMemberTransactions(member.id, txPage, txLimit);
      }
    } catch (error: any) {
      console.error("Error rejecting transaction:", error);
      toast.error(error.message || "ไม่สามารถปฏิเสธรายการได้");
    }
  };

  const handleResend = async (id: string) => {
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error("กรุณาเข้าสู่ระบบใหม่");
        return;
      }

      await TransactionService.resend(token, id);
      toast.success("ส่งรายการซ้ำสำเร็จ");

      // Reload transactions
      if (member) {
        await loadMemberTransactions(member.id, txPage, txLimit);
      }
    } catch (error: any) {
      console.error("Error resending transaction:", error);
      toast.error(error.message || "ไม่สามารถส่งรายการซ้ำได้");
    }
  };

  const handleComplete = async (id: string) => {
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error("กรุณาเข้าสู่ระบบใหม่");
        return;
      }

      await TransactionService.completeV2(token, id);
      toast.success("เสร็จสิ้นรายการสำเร็จ");

      // Reload transactions
      if (member) {
        await loadMemberTransactions(member.id, txPage, txLimit);
      }
    } catch (error: any) {
      console.error("Error completing transaction:", error);
      toast.error(error.message || "ไม่สามารถเสร็จสิ้นรายการได้");
    }
  };

  const handleCancel = async (id: string) => {
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error("กรุณาเข้าสู่ระบบใหม่");
        return;
      }

      await TransactionService.cancelV2(token, id);
      toast.success("ยกเลิกรายการสำเร็จ");

      // Reload transactions
      if (member) {
        await loadMemberTransactions(member.id, txPage, txLimit);
      }
    } catch (error: any) {
      console.error("Error cancelling transaction:", error);
      toast.error(error.message || "ไม่สามารถยกเลิกรายการได้");
    }
  };

  const handleActionClick = (tx: Transaction, action: string) => {
    switch (action) {
      case "approve_manual":
        handleApproveManual(tx.id);
        break;
      case "approve_auto":
        handleApproveAuto(tx.id);
        break;
      case "reject":
        handleReject(tx.id);
        break;
      case "resend":
        handleResend(tx.id);
        break;
      case "complete":
        handleComplete(tx.id);
        break;
      case "cancel":
        handleCancel(tx.id);
        break;
      default:
        console.warn("Unknown action:", action);
    }
  };

  // 📋 ดึง Audit Logs ที่เกี่ยวกับ Member คนนี้ (เฉพาะ action ที่ทำกับลูกค้า ไม่ใช่ login/logout)
  const loadMemberAuditLogs = async (
    memberId: string,
    page: number,
    limit: number
  ) => {
    setIsLoadingAudit(true);
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");

      if (!token) {
        console.error("No session token for loading audit logs");
        return;
      }

      console.log("📋 [MemberDetail] Loading audit logs for member:", memberId);

      const auditService = getAuditLogService();
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

      // 🔍 Query โดยใช้แค่ entityId (ไม่ระบุ entityType เพราะ logs เก่าอาจเป็น 'User' หรือ 'Member')
      // Backend บันทึก entityId: member.id
      const result = await auditService.findAuditLogs(
        token,
        { page: 1, limit: 100 },
        {
          entityId: memberId, // ใช้ entityId เพราะ backend บันทึก entityId: member.id
          dateFrom: oneYearAgo.toISOString().split("T")[0],
          dateTo: new Date().toISOString().split("T")[0],
        }
      );

      console.log(
        "📋 [MemberDetail] Query by entityId:",
        memberId,
        "- found:",
        result.results.length,
        "logs"
      );
      if (result.results.length > 0) {
        console.log(
          "📋 [MemberDetail] Sample logs:",
          result.results.slice(0, 3).map((l) => ({
            action: l.action,
            entityType: l.entityType,
            entityId: l.entityId,
            description: l.description,
          }))
        );
      }

      // 🔍 Filter: เอาเฉพาะ Member/User ที่ไม่ใช่ auth actions
      const authActions = ["login", "logout", "login_failed", "register"];
      const filteredLogs = result.results.filter((log) => {
        // ไม่เอา auth actions
        if (authActions.includes(log.action)) return false;
        // เอาเฉพาะ Member หรือ User (รองรับทั้ง logs เก่าและใหม่)
        if (!["Member", "User"].includes(log.entityType)) return false;
        return true;
      });

      console.log(
        "📋 [MemberDetail] After filter:",
        filteredLogs.length,
        "logs"
      );
      const pagedLogs = filteredLogs.slice(0, limit);

      console.log(
        "✅ [MemberDetail] Loaded",
        pagedLogs.length,
        "audit logs (filtered from",
        result.results.length,
        ")"
      );
      setAuditLogs(pagedLogs);
      setAuditTotal(filteredLogs.length); // ใช้ filtered count
      setAuditPage(page);
    } catch (error) {
      console.error("❌ [MemberDetail] Error loading audit logs:", error);
      setAuditLogs([]);
      setAuditTotal(0);
    } finally {
      setIsLoadingAudit(false);
    }
  };

  // 💰 ดึงธุรกรรมรายวัน และคำนวณสรุป
  const loadDailyFinanceSummary = async (memberId: string, date: Date) => {
    setIsLoadingDailyFinance(true);
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) return;

      // สร้าง date range สำหรับวันนั้น (00:00 - 23:59)
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      console.log(
        "💰 [Finance] Loading transactions for:",
        date.toLocaleDateString("th-TH"),
        { memberId }
      );

      // Query ธุรกรรมของ member ในวันที่เลือก
      const where: any = {
        member_id: {
          __type: "Pointer",
          className: "_User",
          objectId: memberId,
        },
        status: "Approved",
        date_time: {
          $gte: { __type: "Date", iso: startOfDay.toISOString() },
          $lte: { __type: "Date", iso: endOfDay.toISOString() },
        },
      };

      const result = await queryClass(
        "DepositWithdraw",
        {
          where,
          order: "-date_time",
          limit: 100,
        },
        token
      );

      console.log(
        "💰 [Finance] Found",
        result.results?.length || 0,
        "transactions"
      );

      // คำนวณสรุป
      let totalDeposit = 0;
      let totalWithdraw = 0;
      let totalBonus = 0;

      const transactions: DailyTransaction[] = (result.results || []).map(
        (tx: any) => {
          const amount = tx.amount || 0;
          const isBonus = tx.isBonus === true;

          if (tx.type === "Deposit") {
            if (isBonus) {
              totalBonus += amount;
            } else {
              totalDeposit += amount;
            }
          } else if (tx.type === "Withdraw") {
            totalWithdraw += amount;
          }

          return {
            id: tx.objectId,
            type: tx.type,
            amount: amount,
            status: tx.status,
            dateTime: tx.date_time?.iso || tx.date_time,
            channel: tx.chanel || tx.channel,
            isBonus: isBonus,
            promotionCode: tx.promotion_code,
            notes: tx.notes,
          };
        }
      );

      setDailyTransactions(transactions);
      setDailySummary({
        totalDeposit,
        totalWithdraw,
        totalBonus,
        netProfit: totalDeposit - totalWithdraw - totalBonus,
        transactionCount: transactions.length,
      });
    } catch (error) {
      console.error("❌ [Finance] Error loading daily summary:", error);
      setDailyTransactions([]);
      setDailySummary({
        totalDeposit: 0,
        totalWithdraw: 0,
        totalBonus: 0,
        netProfit: 0,
        transactionCount: 0,
      });
    } finally {
      setIsLoadingDailyFinance(false);
    }
  };

  // 🎁 ดึงรายการโปรโมชั่น Active และประวัติการรับโปร
  const loadPromotionData = async (memberId: string) => {
    setIsLoadingPromotions(true);
    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) return;

      console.log("🎁 [Promotion] Loading promotions for member:", memberId);

      // 1. ดึง Active Promotions (โปรที่ส่งได้) - ใช้ Cloud Function ตาม Angular
      try {
        const activePromos = await callCloudFunction(
          "getPromotionActive",
          { id: memberId },
          token
        );
        setActivePromotions(activePromos || []);
        console.log(
          "🎁 [Promotion] Found",
          (activePromos || []).length,
          "active promotions"
        );
      } catch (promoError) {
        console.error(
          "❌ [Promotion] Error loading active promotions:",
          promoError
        );
        // Fallback: query โดยตรง
        const promoResult = await queryClass(
          "Promotion",
          {
            where: { status: "Active", deletedAt: { $exists: false } },
            order: "-createdAt",
            limit: 20,
          },
          token
        );
        setActivePromotions(promoResult.results || []);
      }

      // 2. ดึงประวัติการรับโปรของ member - ใช้ Cloud Function หรือ query ตรง
      try {
        let notifications: any[] = [];

        // ลองใช้ Cloud Function ก่อน
        try {
          const cfResult = await callCloudFunction(
            "getMemberNotification",
            { id: memberId },
            token
          );
          console.log("🎁 [Promotion] Cloud function result:", cfResult);
          notifications = cfResult || [];
        } catch (cfError) {
          console.warn(
            "⚠️ [Promotion] Cloud function failed, using direct query:",
            cfError
          );

          // Fallback: Query class Notification โดยตรง (ตาม Angular: class "Notification")
          const notifResult = await queryClass(
            "Notification",
            {
              where: {
                member_id: {
                  __type: "Pointer",
                  className: "_User",
                  objectId: memberId,
                },
                deletedAt: { $exists: false },
              },
              include: ["user_created_id", "user_updated_id", "member_id"],
              order: "-createdAt",
              limit: 50,
            },
            token
          );
          notifications = notifResult.results || [];
        }

        console.log("🎁 [Promotion] Raw notifications:", notifications);

        // แปลง Parse Objects เป็น plain objects
        const plainNotifications = notifications.map((notif: any) => {
          let plain: any = {};

          if (typeof notif.toJSON === "function") {
            plain = notif.toJSON();
          } else if (typeof notif.get === "function") {
            // Parse Object with .get() method
            plain = {
              objectId: notif.id,
              createdAt: notif.createdAt,
              type: notif.get("type"),
              title: notif.get("title"),
              is_read: notif.get("is_read"),
              member_accepted: notif.get("member_accepted"),
              message_obj: notif.get("message_obj"),
              message: notif.get("message"),
            };

            // Handle user_created_id pointer
            const userCreated = notif.get("user_created_id");
            if (userCreated) {
              if (typeof userCreated.toJSON === "function") {
                plain.user_created_id = userCreated.toJSON();
              } else if (typeof userCreated.get === "function") {
                plain.user_created_id = {
                  objectId: userCreated.id,
                  username: userCreated.get("username"),
                  firstname: userCreated.get("firstname"),
                  role: userCreated.get("role") || userCreated.get("type"),
                };
              } else {
                plain.user_created_id = userCreated;
              }
            }
          } else {
            // Already plain object
            plain = notif;
          }

          return plain;
        });

        setMemberNotifications(plainNotifications);
        console.log(
          "🎁 [Promotion] Found",
          plainNotifications.length,
          "member notifications"
        );
      } catch (notifError) {
        console.error(
          "❌ [Promotion] Error loading member notifications:",
          notifError
        );
        setMemberNotifications([]);
      }
    } catch (error) {
      console.error("❌ [Promotion] Error loading promotion data:", error);
      setActivePromotions([]);
      setMemberNotifications([]);
    } finally {
      setIsLoadingPromotions(false);
    }
  };

  // 🎁 เปิด dialog ยืนยันก่อนส่งโปรโมชั่น
  const confirmSendPromotion = (promoId: string, promoName: string) => {
    setPromoConfirmDialog({
      open: true,
      type: "send",
      promoId,
      promoName,
      notifId: null,
    });
  };

  // 🗑️ เปิด dialog ยืนยันก่อนลบโปรโมชั่น
  const confirmDeleteNotification = (notifId: string, promoName: string) => {
    setPromoConfirmDialog({
      open: true,
      type: "delete",
      promoId: null,
      promoName,
      notifId,
    });
  };

  // ส่งโปรโมชั่นให้ member (หลังจากยืนยันแล้ว)
  const sendPromotion = async (promoId: string) => {
    if (!member) return;
    setIsSendingPromotion(promoId);

    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error("กรุณาเข้าสู่ระบบใหม่");
        return;
      }

      console.log(
        "🎁 [Promotion] Sending promotion:",
        promoId,
        "to member:",
        member.id
      );

      // เรียก Cloud Function sendNewMemberPromotion
      const response: any = await callCloudFunction(
        "sendNewMemberPromotion",
        {
          id: member.id,
          promotion_id: promoId,
        },
        token
      );

      // ตรวจสอบ response
      if (response && response.success) {
        toast.success("ส่งโปรโมชั่นสำเร็จ");
      } else {
        // จัดการ error ตาม Angular
        handleSendPromotionError(response);
        return;
      }

      // Reload ประวัติ
      await loadPromotionData(member.id);
    } catch (error: any) {
      console.error("❌ [Promotion] Error sending promotion:", error);
      // แสดง error message จาก server
      const errorMessage = error.message || "ส่งโปรโมชั่นไม่สำเร็จ";
      toast.error(errorMessage);
    } finally {
      setIsSendingPromotion(null);
    }
  };

  // จัดการ error จากการส่งโปรโมชั่น (ตาม Angular)
  const handleSendPromotionError = (response: any) => {
    let title = "ไม่สามารถส่งโปรโมชั่นได้";
    let message = "";

    switch (response?.reason) {
      case "has_pending_notification":
        title = "มีโปรโมชั่นรออยู่แล้ว";
        message =
          response.message ||
          "สมาชิกมีโปรโมชั่นที่ยังไม่ได้รับอยู่แล้ว กรุณารับหรือปฏิเสธโปรเก่าก่อน";
        break;

      case "has_active_promotion":
        title = "ยังติดเงื่อนไขโปรโมชั่นอยู่";
        message =
          response.message ||
          "สมาชิกยังติดเงื่อนไขโปรโมชั่นอยู่ กรุณาถอนเงินให้จบก่อน";
        if (response.current_promotion_code || response.current_max_withdraw) {
          message += "\n\n📋 รายละเอียดโปรโมชั่นปัจจุบัน:";
          if (response.current_promotion_code) {
            message += `\n• รหัส: ${response.current_promotion_code}`;
          }
          if (response.current_max_withdraw) {
            message += `\n• ถอนได้สูงสุด: ${response.current_max_withdraw.toLocaleString()} บาท`;
          }
        }
        break;

      case "promotion_not_started":
        title = "โปรโมชั่นยังไม่เริ่ม";
        message = response.message || "โปรโมชั่นนี้ยังไม่เริ่มใช้งาน";
        if (response.start_date) {
          message += `\n\n📅 วันที่เริ่ม: ${new Date(
            response.start_date
          ).toLocaleString("th-TH")}`;
        }
        break;

      case "promotion_expired":
        title = "โปรโมชั่นหมดอายุแล้ว";
        message = response.message || "โปรโมชั่นนี้หมดอายุแล้ว";
        if (response.end_date) {
          message += `\n\n📅 วันที่สิ้นสุด: ${new Date(
            response.end_date
          ).toLocaleString("th-TH")}`;
        }
        break;

      case "promotion_inactive":
        title = "โปรโมชั่นไม่สามารถใช้งานได้";
        message = response.message || "โปรโมชั่นนี้ถูกปิดใช้งานชั่วคราว";
        break;

      default:
        message =
          response?.message ||
          response?.error ||
          "เกิดข้อผิดพลาดในการส่งโปรโมชั่น กรุณาลองใหม่อีกครั้ง";
        break;
    }

    // แสดง error ด้วย toast
    toast.error(`${title}: ${message}`);
  };

  // ลบโปรโมชั่นที่ยังไม่ได้รับ (หลังจากยืนยันแล้ว)
  const deletePromotionNotification = async (notifId: string) => {
    if (!member) return;
    setIsDeletingNotification(notifId);

    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) {
        toast.error("กรุณาเข้าสู่ระบบใหม่");
        return;
      }

      console.log("🗑️ [Promotion] Deleting notification:", notifId);

      // เรียก Cloud Function deletePromotionNotification (ตาม Angular)
      const response: any = await callCloudFunction(
        "deletePromotionNotification",
        {
          notification_id: notifId,
        },
        token
      );

      if (response && response.success) {
        toast.success("ยกเลิกโปรโมชั่นสำเร็จ");
      } else {
        toast.error(response?.message || "ยกเลิกโปรโมชั่นไม่สำเร็จ");
        return;
      }

      // Reload ประวัติ
      await loadPromotionData(member.id);
    } catch (error: any) {
      console.error("❌ [Promotion] Error deleting notification:", error);
      toast.error(error.message || "ยกเลิกโปรโมชั่นไม่สำเร็จ");
    } finally {
      setIsDeletingNotification(null);
    }
  };

  // ยืนยันการส่ง/ลบโปรโมชั่น
  const handlePromoConfirm = async () => {
    const { type, promoId, notifId } = promoConfirmDialog;

    // ปิด dialog ก่อน
    setPromoConfirmDialog({
      open: false,
      type: null,
      promoId: null,
      promoName: "",
      notifId: null,
    });

    if (type === "send" && promoId) {
      await sendPromotion(promoId);
    } else if (type === "delete" && notifId) {
      await deletePromotionNotification(notifId);
    }
  };

  // เปิด dialog ยืนยันก่อนบันทึก
  const handleSaveClick = () => {
    setShowConfirmSave(true);
  };

  // บันทึกข้อมูลจริง
  const handleConfirmSave = async () => {
    if (!member) return;

    if (!sessionToken) {
      console.error("No session token found");
      alert("กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    setIsSaving(true);
    try {
      const userService = await getUserService();

      console.log("📝 Saving member:", member.id, formData);

      // ✅ Auto-calculate bank_type based on selected bank
      // bank_type = 2 if TrueWallet, otherwise 1 (regular bank)
      const selectedBank = findBank(formData.bankName);
      const bankType = selectedBank?.id === '19' || selectedBank?.code === 'truewallet' ? 2 : 1;

      // ส่งเฉพาะ fields ที่ backend รองรับ
      await userService.updateMember(sessionToken, member.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        bankName: formData.bankName,
        bankAccount: formData.bankAccount,
        bankType, // ✅ Auto-set bank_type (1=Bank, 2=TrueWallet)
        ...(formData.password ? { password: formData.password } : {}),
      });

      console.log("✅ Member saved successfully");
      setShowConfirmSave(false);
      onSaved?.();
      onOpenChange(false);
    } catch (error) {
      console.error("❌ Error saving member:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + (error as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  // คำนวณการเปลี่ยนแปลง
  const getChanges = () => {
    if (!member) return [];
    const changes: { field: string; oldValue?: string; newValue?: string }[] =
      [];

    if (formData.firstName !== member.firstName) {
      changes.push({
        field: "ชื่อ",
        oldValue: member.firstName,
        newValue: formData.firstName,
      });
    }
    if (formData.lastName !== member.lastName) {
      changes.push({
        field: "นามสกุล",
        oldValue: member.lastName,
        newValue: formData.lastName,
      });
    }
    if (formData.phone !== member.phone) {
      changes.push({
        field: "เบอร์โทร",
        oldValue: member.phone,
        newValue: formData.phone,
      });
    }
    if (formData.bankName !== member.bankName) {
      changes.push({
        field: "ธนาคาร",
        oldValue: member.bankName,
        newValue: formData.bankName,
      });
    }
    if (formData.bankAccount !== member.bankAccount) {
      changes.push({
        field: "เลขบัญชี",
        oldValue: member.bankAccount,
        newValue: formData.bankAccount,
      });
    }
    if (formData.password) {
      changes.push({
        field: "รหัสผ่าน",
        oldValue: "********",
        newValue: "********",
      });
    }

    return changes;
  };

  // ================================================
  // 🎯 Member Action Handlers (เรียก Cloud Functions)
  // ================================================

  // เปิด Confirm Dialog
  const openConfirmDialog = (
    action: MemberAction,
    title: string,
    description: string,
    variant: "default" | "danger" = "default"
  ) => {
    if (!canPerformAction(action)) {
      toast.error("คุณไม่มีสิทธิ์ทำรายการนี้");
      return;
    }
    setConfirmDialog({ open: true, action, title, description, variant });
  };

  // ปิด Confirm Dialog
  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      action: null,
      title: "",
      description: "",
      variant: "default",
    });
  };

  // ยืนยันและทำ Action
  const handleConfirmAction = async () => {
    if (!member || !confirmDialog.action) return;

    const action = confirmDialog.action;
    closeConfirmDialog();
    setActionLoading(action);

    try {
      const token =
        sessionToken ||
        sessionStorage.getItem("admin_session_token") ||
        localStorage.getItem("admin_session_token");
      if (!token) throw new Error("กรุณาเข้าสู่ระบบใหม่");

      let successMessage = "";

      switch (action) {
        case "updateMemberInfo":
          await callCloudFunction("updateMemberInfo", { id: member.id }, token);
          successMessage = "อัพเดทข้อมูลสำเร็จ";
          break;
        case "activeMember":
          await callCloudFunction("activeMember", { id: member.id }, token);
          successMessage = "เปิดใช้งานลูกค้าสำเร็จ";
          break;
        case "suspendMember":
          await callCloudFunction("suspendMember", { id: member.id }, token);
          successMessage = "ปิดใช้งานลูกค้าสำเร็จ";
          break;
        case "resetTurnOverCash":
          await callCloudFunction(
            "resetTurnOverCashMember",
            { id: member.id },
            token
          );
          successMessage = "รีเซตเทินสำเร็จ";
          break;
        case "resetPromotion":
          await callCloudFunction("resetPromotion", { id: member.id }, token);
          successMessage = "รีเซตโปรโมชันสำเร็จ";
          break;
        case "resetPerDayCondition":
          await callCloudFunction(
            "resetPerDayCondition",
            { id: member.id },
            token
          );
          successMessage = "รีเซตเงื่อนไขถอนต่อวันสำเร็จ";
          break;
        case "clearCredit":
          await callCloudFunction(
            "clearMemberCredit",
            { id: member.id },
            token
          );
          successMessage = "ส่งคำขอดึงเครดิตสำเร็จ กรุณารอระบบดำเนินการ";
          break;
      }

      toast.success(successMessage);

      // 🔄 Refresh member data ทันที (ไม่ต้องรอ autoUpdate)
      await refreshMemberData(member.id);

      // Notify parent to refresh list
      onSaved?.();
    } catch (error) {
      console.error(`❌ Action ${action} failed:`, error);
      toast.error("เกิดข้อผิดพลาด: " + (error as Error).message);
    } finally {
      setActionLoading(null);
    }
  };

  // Copy to Clipboard พร้อมแสดง feedback
  const copyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.success(`คัดลอก ${fieldName} แล้ว`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (!member) return null;

  // Calculate values
  const sumDW = (member.sumDepositWithdraw || {}) as Record<string, number>;
  const sumWL = (member.sumWinlose || {}) as Record<string, number>;
  const totalDeposit = sumDW.sum_deposit_cash_all || 0;
  const totalWithdraw = sumDW.sum_withdraw_cash_all || 0;
  const totalBonus = sumDW.sum_deposit_bonus_all || 0;
  const totalWinLose = sumWL.sum_wl_all || 0;
  const balance = member.balance || 0;
  const turnoverCash = sumDW.sum_turn_over_cash || 0;
  const turnoverBonus = sumDW.sum_turn_over_bonus || 0;
  const turnover = turnoverCash + turnoverBonus;
  const validBet = (sumWL.sum_vl_before_w || 0) * -1;
  const turnoverRemaining = turnover - validBet;
  const netProfit = totalDeposit - totalWithdraw - totalBonus;
  const hasPromotion = !!(
    member.promotion?.max_withdraw && member.promotion.max_withdraw > 0
  );

  // Status info
  const statusLabels: Record<string, { label: string; color: string }> = {
    Active: { label: "ปกติ", color: "bg-emerald-500" },
    Suspend: { label: "ระงับ", color: "bg-red-500" },
    Watchout: { label: "เฝ้าระวัง", color: "bg-amber-500" },
    Inactive: { label: "ไม่เคลื่อนไหว", color: "bg-gray-500" },
  };
  const statusInfo = statusLabels[member.status] || {
    label: member.status,
    color: "bg-gray-500",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header - Responsive with Copy Buttons */}
        <div className="flex-shrink-0 border-b bg-gradient-to-r from-primary/5 to-primary/10 p-3 sm:p-4">
          {/* Syncing Indicator */}
          {isUpdatingInfo && (
            <div className="flex items-center justify-center gap-2 text-xs text-blue-600 mb-2 bg-blue-50 dark:bg-blue-950/30 py-1.5 px-3 rounded-full w-fit mx-auto">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>กำลังซิงค์ข้อมูลล่าสุด...</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-primary-foreground text-lg sm:text-xl font-bold">
                  {(member.firstName?.[0] || "U").toUpperCase()}
                </div>
                <div
                  className={cn(
                    "absolute -bottom-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-background",
                    statusInfo.color
                  )}
                />
              </div>

              {/* Info */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-base sm:text-lg font-bold truncate">
                    {member.firstName} {member.lastName}
                  </h2>
                  <Badge
                    variant={
                      member.status === "Active" ? "default" : "secondary"
                    }
                    className="text-xs"
                  >
                    {statusInfo.label}
                  </Badge>
                  {hasPromotion && (
                    <Badge
                      variant="outline"
                      className="bg-orange-50 text-orange-700 border-orange-200 text-xs"
                    >
                      <Gift className="w-3 h-3 mr-1" />
                      ติดโปร
                    </Badge>
                  )}
                </div>

                {/* Username & Phone with Copy */}
                <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm mt-1.5">
                  {/* Agent Username (ปุ่ม Copy ที่สำคัญ) */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() =>
                          copyToClipboard(
                            member.agentUsername || "",
                            "Username"
                          )
                        }
                        className={cn(
                          "flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors font-mono",
                          "bg-primary/10 hover:bg-primary/20 text-primary",
                          copiedField === "Username" &&
                            "bg-emerald-100 text-emerald-700"
                        )}
                      >
                        <Gamepad2 className="w-3.5 h-3.5" />
                        <span className="font-semibold">
                          {member.agentUsername || "-"}
                        </span>
                        {copiedField === "Username" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5 opacity-50" />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>คลิกเพื่อคัดลอก Username</TooltipContent>
                  </Tooltip>

                  {/* Phone - ปุ่ม Copy ที่ชัดเจน */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() =>
                          copyToClipboard(member.phone || "", "เบอร์โทร")
                        }
                        className={cn(
                          "flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors",
                          "bg-muted hover:bg-muted/80",
                          copiedField === "เบอร์โทร" &&
                            "bg-emerald-100 text-emerald-700"
                        )}
                      >
                        <Phone className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline font-medium">
                          {member.phone}
                        </span>
                        <span className="sm:hidden font-medium">
                          {member.phone?.slice(-4)}
                        </span>
                        {copiedField === "เบอร์โทร" ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="w-3.5 h-3.5 opacity-50" />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>คลิกเพื่อคัดลอกเบอร์โทร</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>

            {/* Credit Display */}
            <div className="text-right flex-shrink-0 sm:ml-auto">
              <div className="text-xs text-muted-foreground">เครดิต</div>
              <div className="text-xl sm:text-2xl font-bold text-emerald-600">
                {formatCurrency(balance)}
              </div>
              {lastUpdateTime && (
                <div className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                  <Clock className="w-3 h-3" />
                  ซิงค์ {lastUpdateTime}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs - Responsive */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex-1 overflow-hidden flex flex-col"
        >
          <TabsList className="grid grid-cols-8 w-full flex-shrink-0 rounded-none border-b bg-muted/30 p-0.5 sm:p-1 gap-0.5 sm:gap-1">
            <TabsTrigger
              value="overview"
              className="flex items-center justify-center gap-1 text-xs sm:text-sm data-[state=active]:bg-background px-1 sm:px-3 py-1.5 sm:py-2"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span className="hidden sm:inline">ภาพรวม</span>
            </TabsTrigger>
            <TabsTrigger
              value="finance"
              className="flex items-center justify-center gap-1 text-xs sm:text-sm data-[state=active]:bg-background px-1 sm:px-3 py-1.5 sm:py-2"
              onClick={() =>
                member &&
                loadDailyFinanceSummary(member.id, selectedFinanceDate)
              }
            >
              <Banknote className="w-4 h-4" />
              <span className="hidden sm:inline">การเงิน</span>
            </TabsTrigger>
            <TabsTrigger
              value="promotion"
              className="flex items-center justify-center gap-1 text-xs sm:text-sm data-[state=active]:bg-background px-1 sm:px-3 py-1.5 sm:py-2"
              onClick={() => member && loadPromotionData(member.id)}
            >
              <Gift className="w-4 h-4" />
              <span className="hidden sm:inline">โปร</span>
            </TabsTrigger>
            <TabsTrigger
              value="transactions"
              className="flex items-center justify-center gap-1 text-xs sm:text-sm data-[state=active]:bg-background px-1 sm:px-3 py-1.5 sm:py-2"
            >
              <Receipt className="w-4 h-4" />
              <span className="hidden sm:inline">ธุรกรรม</span>
            </TabsTrigger>
            <TabsTrigger
              value="referral"
              className="flex items-center justify-center gap-1 text-xs sm:text-sm data-[state=active]:bg-background px-1 sm:px-3 py-1.5 sm:py-2"
              onClick={() => member && loadMemberReferral(member.id)}
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">แนะนำ</span>
            </TabsTrigger>
            <TabsTrigger
              value="bethistory"
              className="flex items-center justify-center gap-1 text-xs sm:text-sm data-[state=active]:bg-background px-1 sm:px-3 py-1.5 sm:py-2"
            >
              <Gamepad2 className="w-4 h-4" />
              <span className="hidden sm:inline">เกม</span>
            </TabsTrigger>
            <TabsTrigger
              value="audit"
              className="flex items-center justify-center gap-1 text-xs sm:text-sm data-[state=active]:bg-background px-1 sm:px-3 py-1.5 sm:py-2"
              onClick={() =>
                member && loadMemberAuditLogs(member.id, 1, auditLimit)
              }
            >
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">กิจกรรม</span>
            </TabsTrigger>
            <TabsTrigger
              value="edit"
              className="flex items-center justify-center gap-1 text-xs sm:text-sm data-[state=active]:bg-background px-1 sm:px-3 py-1.5 sm:py-2"
            >
              <UserCog className="w-4 h-4" />
              <span className="hidden sm:inline">แก้ไข</span>
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto p-3 sm:p-4">
            {/* ============================================ */}
            {/* TAB 1: ภาพรวม (Overview) - Most Important */}
            {/* ============================================ */}
            <TabsContent value="overview" className="m-0 space-y-4">
              {/* Key Metrics - Responsive Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-950/30 dark:to-emerald-900/20 border-emerald-200">
                  <CardContent className="p-4 text-center">
                    <ArrowDownCircle className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                    <div className="text-xs text-muted-foreground">
                      ยอดฝากรวม
                    </div>
                    <div className="text-lg font-bold text-emerald-600">
                      {formatCurrency(totalDeposit)}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-950/30 dark:to-red-900/20 border-red-200">
                  <CardContent className="p-4 text-center">
                    <ArrowUpCircle className="w-6 h-6 text-red-600 mx-auto mb-1" />
                    <div className="text-xs text-muted-foreground">
                      ยอดถอนรวม
                    </div>
                    <div className="text-lg font-bold text-red-600">
                      {formatCurrency(totalWithdraw)}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 border-amber-200">
                  <CardContent className="p-4 text-center">
                    <TrendingUp className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                    <div className="text-xs text-muted-foreground">แพ้/ชนะ</div>
                    <div
                      className={cn(
                        "text-lg font-bold",
                        totalWinLose >= 0 ? "text-emerald-600" : "text-red-600"
                      )}
                    >
                      {formatCurrency(totalWinLose)}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <PiggyBank className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                    <div className="text-xs text-muted-foreground">
                      กำไรเว็บ
                    </div>
                    <div
                      className={cn(
                        "text-lg font-bold",
                        netProfit >= 0 ? "text-emerald-600" : "text-red-600"
                      )}
                    >
                      {formatCurrency(netProfit)}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Two Column Layout - Responsive */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {/* Left: Bank & Contact Info */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-emerald-500" />
                      ข้อมูลบัญชี
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <BankLogo bankValue={member.bankName} size="lg" />
                      <div className="flex-1">
                        <div className="font-mono font-bold text-lg">
                          {member.bankAccount || "-"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {findBank(member.bankName)?.name || "ไม่ระบุธนาคาร"}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          copyToClipboard(member.bankAccount, "เลขบัญชี")
                        }
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* 📅 วันที่สมัคร */}
                    {member.createdAt && (
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900">
                        <div className="p-2 rounded-lg bg-blue-500/10">
                          <UserPlus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-muted-foreground">
                            วันที่สมัคร
                          </div>
                          <div className="font-medium">
                            {(() => {
                              const date = new Date(member.createdAt);
                              const thaiMonths = [
                                "มกราคม",
                                "กุมภาพันธ์",
                                "มีนาคม",
                                "เมษายน",
                                "พฤษภาคม",
                                "มิถุนายน",
                                "กรกฎาคม",
                                "สิงหาคม",
                                "กันยายน",
                                "ตุลาคม",
                                "พฤศจิกายน",
                                "ธันวาคม",
                              ];
                              const day = date.getDate();
                              const month = thaiMonths[date.getMonth()];
                              const year = date.getFullYear() + 543;
                              const time = date.toLocaleTimeString("th-TH", {
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                              return `${day} ${month} ${year} เวลา ${time} น.`;
                            })()}
                          </div>
                          <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                            {(() => {
                              const date = new Date(member.createdAt);
                              const now = new Date();
                              const diffTime = Math.abs(
                                now.getTime() - date.getTime()
                              );
                              const diffDays = Math.floor(
                                diffTime / (1000 * 60 * 60 * 24)
                              );

                              if (diffDays === 0) return "🆕 สมัครวันนี้";
                              if (diffDays === 1) return "📅 สมัครเมื่อวาน";
                              if (diffDays < 7)
                                return `📅 สมัครมา ${diffDays} วัน`;
                              if (diffDays < 30)
                                return `📅 สมัครมา ${Math.floor(
                                  diffDays / 7
                                )} สัปดาห์`;
                              if (diffDays < 365)
                                return `📅 สมัครมา ${Math.floor(
                                  diffDays / 30
                                )} เดือน`;
                              return `📅 สมัครมา ${Math.floor(
                                diffDays / 365
                              )} ปี ${Math.floor((diffDays % 365) / 30)} เดือน`;
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Right: Promotion Status */}
                <Card
                  className={cn(
                    hasPromotion &&
                      "border-orange-200 bg-orange-50/50 dark:bg-orange-950/20"
                  )}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Gift className="w-4 h-4 text-orange-500" />
                      สถานะโปรโมชัน
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {hasPromotion ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Badge className="bg-orange-500 text-white text-lg px-3 py-1">
                            {member.promotion?.promotion_code ||
                              member.promotion?.code ||
                              "PROMO"}
                          </Badge>
                          <div className="text-right">
                            <div className="text-xs text-muted-foreground">
                              ถอนได้สูงสุด
                            </div>
                            <div className="font-bold text-red-600">
                              {formatCurrency(
                                member.promotion?.max_withdraw || 0
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              เทิร์นที่ทำแล้ว
                            </span>
                            <span className="font-medium text-blue-600">
                              {formatCurrency(validBet)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              เทิร์นต้องทำ
                            </span>
                            <span className="font-medium text-orange-600">
                              {formatCurrency(turnover)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm font-bold">
                            <span>เทิร์นเหลือ</span>
                            <span
                              className={
                                turnoverRemaining > 0
                                  ? "text-red-600"
                                  : "text-emerald-600"
                              }
                            >
                              {formatCurrency(turnoverRemaining)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                        <div className="font-medium text-emerald-600">
                          ไม่ติดโปรโมชัน
                        </div>
                        <div className="text-xs text-muted-foreground">
                          สามารถถอนได้ตามปกติ
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* ========================================= */}
              {/* 🎯 Quick Actions - ปุ่มจัดการด่วน (ย้ายมาล่าง) */}
              {/* ========================================= */}
              <Card className="border-2 border-dashed">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Settings className="w-4 h-4 text-primary" />
                    การจัดการด่วน
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {/* เปิดใช้งาน */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-auto py-2 flex-col gap-1 border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                    onClick={() =>
                      openConfirmDialog(
                        "activeMember",
                        "เปิดใช้งานลูกค้า",
                        "ยืนยันการเปิดใช้งานลูกค้ารายนี้?"
                      )
                    }
                    disabled={
                      actionLoading === "activeMember" ||
                      member.status === "Active" ||
                      !canPerformAction("activeMember")
                    }
                  >
                    {actionLoading === "activeMember" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Power className="w-4 h-4" />
                    )}
                    <span className="text-xs">เปิดใช้งาน</span>
                  </Button>

                  {/* ปิดใช้งาน */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-auto py-2 flex-col gap-1 border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                    onClick={() =>
                      openConfirmDialog(
                        "suspendMember",
                        "ปิดใช้งานลูกค้า",
                        "ยืนยันการปิดใช้งานลูกค้ารายนี้?",
                        "danger"
                      )
                    }
                    disabled={
                      actionLoading === "suspendMember" ||
                      member.status === "Suspend" ||
                      !canPerformAction("suspendMember")
                    }
                  >
                    {actionLoading === "suspendMember" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <PowerOff className="w-4 h-4" />
                    )}
                    <span className="text-xs">ปิดใช้งาน</span>
                  </Button>

                  {/* รีเซตโปรโมชัน */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-auto py-2 flex-col gap-1 border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                    onClick={() =>
                      openConfirmDialog(
                        "resetPromotion",
                        "รีเซตโปรโมชัน",
                        "ลูกค้าจะหลุดจากเงื่อนไขโปรโมชัน และถอนได้ตามปกติ"
                      )
                    }
                    disabled={
                      actionLoading === "resetPromotion" ||
                      !canPerformAction("resetPromotion")
                    }
                  >
                    {actionLoading === "resetPromotion" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RotateCcw className="w-4 h-4" />
                    )}
                    <span className="text-xs">รีเซตโปร</span>
                  </Button>

                  {/* รีเซตเทิน */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-auto py-2 flex-col gap-1 border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 disabled:opacity-50"
                    onClick={() =>
                      openConfirmDialog(
                        "resetTurnOverCash",
                        "รีเซตเทิน",
                        "ยอดเทิร์นโอเวอร์จะถูกรีเซตเป็น 0",
                        "danger"
                      )
                    }
                    disabled={
                      actionLoading === "resetTurnOverCash" ||
                      !canPerformAction("resetTurnOverCash")
                    }
                  >
                    {actionLoading === "resetTurnOverCash" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Target className="w-4 h-4" />
                    )}
                    <span className="text-xs">รีเซตเทิน</span>
                  </Button>

                  {/* รีเซตเงื่อนไขถอนต่อวัน */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-auto py-2 flex-col gap-1 border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:opacity-50"
                    onClick={() =>
                      openConfirmDialog(
                        "resetPerDayCondition",
                        "รีเซตเงื่อนไขถอนต่อวัน",
                        "เงื่อนไขการถอนต่อวันจะถูกรีเซต"
                      )
                    }
                    disabled={
                      actionLoading === "resetPerDayCondition" ||
                      !canPerformAction("resetPerDayCondition")
                    }
                  >
                    {actionLoading === "resetPerDayCondition" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CalendarIcon className="w-4 h-4" />
                    )}
                    <span className="text-xs">รีเซตถอนรายวัน</span>
                  </Button>

                  {/* ดึงเครดิตกลับ */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-auto py-2 flex-col gap-1 border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50"
                    onClick={() =>
                      openConfirmDialog(
                        "clearCredit",
                        "ดึงเครดิตกลับ",
                        `ยืนยันการดึงเครดิตของ ${member.firstName} ${
                          member.lastName
                        } (${formatCurrency(
                          member.balance || 0
                        )}) กลับเข้าระบบทั้งหมด`,
                        "danger"
                      )
                    }
                    disabled={
                      actionLoading === "clearCredit" ||
                      !canPerformAction("clearCredit") ||
                      (member.balance || 0) <= 0
                    }
                  >
                    {actionLoading === "clearCredit" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <AlertTriangle className="w-4 h-4" />
                    )}
                    <span className="text-xs">ดึงเครดิต</span>
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ============================================ */}
            {/* TAB 2: การเงิน (Finance) */}
            {/* ============================================ */}
            <TabsContent value="finance" className="m-0 space-y-4">
              {/* 📅 Date Picker + Daily Summary Header */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium">สรุปรายวัน:</span>

                  {/* Date Picker */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[200px] justify-start text-left font-normal",
                          !selectedFinanceDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedFinanceDate ? (
                          selectedFinanceDate.toLocaleDateString("th-TH", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        ) : (
                          <span>เลือกวันที่</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedFinanceDate}
                        onSelect={(date) => {
                          if (date && member) {
                            setSelectedFinanceDate(date);
                            loadDailyFinanceSummary(member.id, date);
                          }
                        }}
                        disabled={(date) => date > new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    member &&
                    loadDailyFinanceSummary(member.id, selectedFinanceDate)
                  }
                  disabled={isLoadingDailyFinance}
                >
                  {isLoadingDailyFinance ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                </Button>
              </div>

              {/* 📊 Daily Summary Cards (5 boxes) */}
              <div className="grid grid-cols-5 gap-3">
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-3 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <ArrowDownCircle className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="text-xs text-muted-foreground mb-1">
                      ฝากเงินสด
                    </div>
                    <div className="text-lg font-bold text-emerald-600">
                      {isLoadingDailyFinance
                        ? "-"
                        : formatCurrency(dailySummary.totalDeposit)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-3 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="text-xs text-muted-foreground mb-1">
                      โบนัส
                    </div>
                    <div className="text-lg font-bold text-purple-600">
                      {isLoadingDailyFinance
                        ? "-"
                        : formatCurrency(dailySummary.totalBonus)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-3 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <ArrowUpCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="text-xs text-muted-foreground mb-1">
                      ถอนเงิน
                    </div>
                    <div className="text-lg font-bold text-red-600">
                      {isLoadingDailyFinance
                        ? "-"
                        : formatCurrency(dailySummary.totalWithdraw)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-3 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="text-xs text-muted-foreground mb-1">
                      รายการ
                    </div>
                    <div className="text-lg font-bold text-amber-600">
                      {isLoadingDailyFinance
                        ? "-"
                        : dailySummary.transactionCount}
                    </div>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-3 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-xs text-muted-foreground mb-1">
                      กำไร/ขาดทุน
                    </div>
                    <div
                      className={cn(
                        "text-lg font-bold",
                        dailySummary.netProfit >= 0
                          ? "text-emerald-600"
                          : "text-red-600"
                      )}
                    >
                      {isLoadingDailyFinance ? (
                        "-"
                      ) : (
                        <>
                          {dailySummary.netProfit >= 0 ? "+" : ""}
                          {formatCurrency(dailySummary.netProfit)}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* 📋 Daily Transactions List */}
              {dailyTransactions.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Receipt className="w-4 h-4" />
                      ธุรกรรมวันที่{" "}
                      {selectedFinanceDate.toLocaleDateString("th-TH")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-[200px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">
                              เวลา
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                              ประเภท
                            </th>
                            <th className="px-3 py-2 text-right font-medium">
                              จำนวน
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                              ช่องทาง
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {dailyTransactions.map((tx) => (
                            <tr key={tx.id} className="hover:bg-muted/30">
                              <td className="px-3 py-2 text-muted-foreground">
                                {tx.dateTime
                                  ? new Date(tx.dateTime).toLocaleTimeString(
                                      "th-TH",
                                      { hour: "2-digit", minute: "2-digit" }
                                    )
                                  : "-"}
                              </td>
                              <td className="px-3 py-2">
                                <Badge
                                  className={cn(
                                    "gap-1",
                                    tx.type === "Deposit"
                                      ? "bg-emerald-100 text-emerald-800"
                                      : "bg-red-100 text-red-800"
                                  )}
                                >
                                  {tx.type === "Deposit" ? "ฝาก" : "ถอน"}
                                </Badge>
                                {tx.isBonus && (
                                  <Badge
                                    variant="outline"
                                    className="ml-1 text-purple-600"
                                  >
                                    โบนัส
                                  </Badge>
                                )}
                              </td>
                              <td
                                className={cn(
                                  "px-3 py-2 text-right font-bold",
                                  tx.type === "Deposit"
                                    ? "text-emerald-600"
                                    : "text-red-600"
                                )}
                              >
                                {tx.type === "Deposit" ? "+" : "-"}
                                {formatCurrency(tx.amount)}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">
                                {tx.channel || "Web"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Summary Table - ข้อมูลรวมทั้งหมด */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Wallet className="w-4 h-4" />
                    สรุปข้อมูลการเงิน (ทั้งหมด)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="px-3 py-2 text-left font-medium">
                          รายการ
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          เงินสด
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          โบนัส
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          รวม
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr>
                        <td className="px-3 py-3 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500" />
                          ยอดฝาก
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-emerald-600">
                          +{formatCurrency(totalDeposit)}
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-purple-600">
                          +{formatCurrency(totalBonus)}
                        </td>
                        <td className="px-3 py-3 text-right font-bold text-emerald-600">
                          {formatCurrency(totalDeposit + totalBonus)}
                        </td>
                      </tr>
                      <tr className="bg-muted/20">
                        <td className="px-3 py-3 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-red-500" />
                          ยอดถอน
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-red-600">
                          -{formatCurrency(totalWithdraw)}
                        </td>
                        <td className="px-3 py-3 text-right text-muted-foreground">
                          0.00
                        </td>
                        <td className="px-3 py-3 text-right font-bold text-red-600">
                          -{formatCurrency(totalWithdraw)}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-3 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-amber-500" />
                          แพ้/ชนะ
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-amber-600">
                          {formatCurrency(totalWinLose)}
                        </td>
                        <td className="px-3 py-3 text-right text-muted-foreground">
                          -
                        </td>
                        <td className="px-3 py-3 text-right font-bold text-amber-600">
                          {formatCurrency(totalWinLose)}
                        </td>
                      </tr>
                      <tr className="bg-blue-50 dark:bg-blue-950/30 font-bold">
                        <td className="px-3 py-3 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-blue-600" />
                          คงเหลือ
                        </td>
                        <td className="px-3 py-3 text-right text-blue-600">
                          {formatCurrency(
                            totalDeposit - totalWithdraw + totalWinLose
                          )}
                        </td>
                        <td className="px-3 py-3 text-right text-purple-600">
                          {formatCurrency(totalBonus)}
                        </td>
                        <td className="px-3 py-3 text-right text-lg text-blue-600">
                          {formatCurrency(
                            totalDeposit -
                              totalWithdraw +
                              totalWinLose +
                              totalBonus
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* Turnover & Betting Stats */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Target className="w-4 h-4 text-orange-500" />
                      Turn Over
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-center p-2 rounded bg-muted/30">
                      <span className="text-sm text-muted-foreground">
                        Turn Over เงินสด
                      </span>
                      <span className="font-medium">
                        {formatCurrency(turnoverCash)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded bg-muted/30">
                      <span className="text-sm text-muted-foreground">
                        Turn Over โบนัส
                      </span>
                      <span className="font-medium">
                        {formatCurrency(turnoverBonus)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded bg-orange-50 dark:bg-orange-950/30">
                      <span className="text-sm font-medium">
                        เทิร์นเหลือก่อนถอน
                      </span>
                      <span
                        className={cn(
                          "font-bold",
                          turnoverRemaining > 0
                            ? "text-red-600"
                            : "text-emerald-600"
                        )}
                      >
                        {formatCurrency(turnoverRemaining)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Gamepad2 className="w-4 h-4 text-blue-500" />
                      สถิติการเดิมพัน
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between items-center p-2 rounded bg-muted/30">
                      <span className="text-sm text-muted-foreground">
                        ยอดเดิมพันรวม
                      </span>
                      <span className="font-medium">
                        {formatCurrency((sumWL.sum_vl_all || 0) * -1)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded bg-muted/30">
                      <span className="text-sm text-muted-foreground">
                        ยอดเดิมพันวันนี้
                      </span>
                      <span className="font-medium">
                        {formatCurrency((sumWL.sum_vl_today || 0) * -1)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-2 rounded bg-blue-50 dark:bg-blue-950/30">
                      <span className="text-sm font-medium">
                        ยอดเดิมพันปัจจุบัน
                      </span>
                      <span className="font-bold text-blue-600">
                        {formatCurrency(validBet)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Referral */}
              {memberRefer && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Users className="w-4 h-4 text-sky-500" />
                      รายได้แนะนำเพื่อน
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-3 rounded-lg bg-muted/30">
                        <div className="text-2xl font-bold text-sky-600">
                          {(memberRefer?.level1Count || 0) +
                            (memberRefer?.level2Count || 0)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          เพื่อนทั้งหมด
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/30">
                        <div className="text-2xl font-bold text-emerald-600">
                          {formatCurrency(
                            (memberRefer?.level1Income || 0) +
                              (memberRefer?.level2Income || 0)
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          รายได้รวม
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/30">
                        <div className="text-2xl font-bold text-amber-600">
                          0.00
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ถอนแล้ว
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* ============================================ */}
            {/* TAB 3: โปรโมชัน */}
            {/* ============================================ */}
            <TabsContent value="promotion" className="m-0 space-y-4">
              {/* Current Promotion Status */}
              <Card className={cn(hasPromotion && "border-orange-200")}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Gift className="w-5 h-5 text-orange-500" />
                    สถานะปัจจุบัน
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {hasPromotion ? (
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="text-center p-4 rounded-lg bg-orange-50 dark:bg-orange-950/30">
                          <Badge className="bg-orange-500 text-white text-xl px-4 py-2 mb-2">
                            {member.promotion?.promotion_code || "PROMO"}
                          </Badge>
                          <div className="text-sm text-muted-foreground mt-2">
                            รหัสโปรโมชัน
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between p-2 bg-muted/30 rounded">
                            <span>ถอนได้สูงสุด</span>
                            <span className="font-bold text-red-600">
                              {formatCurrency(
                                member.promotion?.max_withdraw || 0
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="p-4 rounded-lg bg-muted/30 text-center">
                          <div className="text-3xl font-bold text-emerald-600">
                            {formatCurrency(balance)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            เครดิตคงเหลือ
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>เทิร์นที่ทำแล้ว</span>
                            <span className="font-medium text-blue-600">
                              {formatCurrency(validBet)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>เทิร์นต้องทำ</span>
                            <span className="font-medium text-orange-600">
                              {formatCurrency(turnover)}
                            </span>
                          </div>
                          <div className="flex justify-between font-bold border-t pt-2">
                            <span>เทิร์นเหลือ</span>
                            <span
                              className={
                                turnoverRemaining > 0
                                  ? "text-red-600"
                                  : "text-emerald-600"
                              }
                            >
                              {formatCurrency(turnoverRemaining)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-3" />
                      <div className="text-xl font-bold text-emerald-600 mb-1">
                        ไม่ติดโปรโมชัน
                      </div>
                      <div className="text-muted-foreground">
                        ลูกค้าสามารถถอนเงินได้ตามปกติ
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="h-auto py-4 border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                  onClick={() =>
                    openConfirmDialog(
                      "resetPromotion",
                      "รีเซตโปรโมชัน",
                      "ลูกค้าจะหลุดจากเงื่อนไขโปรโมชัน และถอนได้ตามปกติ"
                    )
                  }
                  disabled={actionLoading === "resetPromotion"}
                >
                  {actionLoading === "resetPromotion" ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <RotateCcw className="w-5 h-5 mr-2" />
                  )}
                  <div className="text-left">
                    <div className="font-medium">รีเซตสถานะโปรโมชัน</div>
                    <div className="text-xs opacity-70">ล้างสถานะติดโปร</div>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto py-4 border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                  onClick={() =>
                    openConfirmDialog(
                      "resetTurnOverCash",
                      "รีเซตเทิน",
                      "ยอดเทิร์นโอเวอร์จะถูกรีเซตเป็น 0",
                      "danger"
                    )
                  }
                  disabled={actionLoading === "resetTurnOverCash"}
                >
                  {actionLoading === "resetTurnOverCash" ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Target className="w-5 h-5 mr-2" />
                  )}
                  <div className="text-left">
                    <div className="font-medium">รีเซตเทิร์นโอเวอร์</div>
                    <div className="text-xs opacity-70">
                      รีเซตยอดเทิร์นเป็น 0
                    </div>
                  </div>
                </Button>
              </div>

              {/* 🎁 Send Promotion Section */}
              <Card>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Gift className="w-4 h-4 text-emerald-600" />
                    ส่งโปรโมชั่น
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => member && loadPromotionData(member.id)}
                    disabled={isLoadingPromotions}
                  >
                    {isLoadingPromotions ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                </CardHeader>
                <CardContent>
                  {isLoadingPromotions ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : activePromotions.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <Gift className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <div>ไม่มีโปรโมชั่นที่สามารถส่งได้</div>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {activePromotions.map((promo) => (
                        <div
                          key={promo.objectId}
                          className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                              <Gift className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div>
                              <div className="font-medium text-sm">
                                {promo.name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {promo.description}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="bg-amber-50 text-amber-700 border-amber-200"
                            >
                              {promo.code}
                            </Badge>
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() =>
                                confirmSendPromotion(promo.objectId, promo.name)
                              }
                              disabled={isSendingPromotion === promo.objectId}
                            >
                              {isSendingPromotion === promo.objectId ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                "ส่ง"
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* 📜 Promotion History */}
              <Card>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <History className="w-4 h-4" />
                    ประวัติโปรโมชั่น
                    {memberNotifications.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {memberNotifications.length}
                      </Badge>
                    )}
                  </CardTitle>
                  {memberNotifications.length > 10 && (
                    <Select
                      value={String(promoHistoryLimit)}
                      onValueChange={(v) => setPromoHistoryLimit(Number(v))}
                    >
                      <SelectTrigger className="w-[80px] h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  {memberNotifications.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <div className="text-sm">ไม่มีประวัติ</div>
                    </div>
                  ) : (
                    <div className="max-h-[280px] overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50 sticky top-0 z-10">
                          <tr>
                            <th className="px-2 py-2 text-left font-medium">
                              วันที่
                            </th>
                            <th className="px-2 py-2 text-left font-medium">
                              ผู้ส่ง
                            </th>
                            <th className="px-2 py-2 text-center font-medium w-[60px]">
                              สถานะ
                            </th>
                            <th className="px-2 py-2 text-left font-medium">
                              โปรโมชั่น
                            </th>
                            <th className="px-2 py-2 text-center font-medium w-[50px]"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                          {memberNotifications
                            .slice(0, promoHistoryLimit)
                            .map((notif, idx) => {
                              const isRead = notif.is_read === true;
                              const memberAccepted = notif.member_accepted;
                              const promoCode =
                                notif.message_obj?.promotion_code ||
                                notif.message?.promotion_code ||
                                "-";
                              const title =
                                notif.title || notif.message_obj?.title || "";
                              const createdAt =
                                notif.createdAt?.iso || notif.createdAt;
                              const sender =
                                notif.user_created_id?.username ||
                                notif.user_created_id?.firstname ||
                                "Sys";

                              return (
                                <tr
                                  key={notif.objectId || idx}
                                  className="hover:bg-muted/30 transition-colors"
                                >
                                  {/* วันที่ */}
                                  <td className="px-2 py-1.5">
                                    <div className="text-gray-900 dark:text-gray-100">
                                      {createdAt
                                        ? new Date(
                                            createdAt
                                          ).toLocaleDateString("th-TH", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "2-digit",
                                          })
                                        : "-"}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground">
                                      {createdAt
                                        ? new Date(
                                            createdAt
                                          ).toLocaleTimeString("th-TH", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })
                                        : ""}
                                    </div>
                                  </td>

                                  {/* ผู้ส่ง */}
                                  <td className="px-2 py-1.5">
                                    <div className="flex items-center gap-1.5">
                                      <User className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                      <span className="text-gray-700 dark:text-gray-300">
                                        {sender}
                                      </span>
                                    </div>
                                  </td>

                                  {/* สถานะ */}
                                  <td className="px-2 py-1.5 text-center">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="inline-flex items-center gap-0.5">
                                          {isRead ? (
                                            <Eye className="w-3 h-3 text-blue-500" />
                                          ) : (
                                            <EyeOff className="w-3 h-3 text-gray-300" />
                                          )}
                                          {memberAccepted === 0 && (
                                            <span className="w-2 h-2 rounded-full bg-amber-400" />
                                          )}
                                          {memberAccepted === 1 && (
                                            <CheckCircle className="w-3 h-3 text-emerald-500" />
                                          )}
                                          {memberAccepted === 2 && (
                                            <span className="w-2 h-2 rounded-full bg-red-500" />
                                          )}
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>
                                          {isRead ? "อ่านแล้ว" : "ยังไม่อ่าน"} •
                                          {memberAccepted === 0 && " รอยืนยัน"}
                                          {memberAccepted === 1 && " รับแล้ว"}
                                          {memberAccepted === 2 && " ปฏิเสธ"}
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </td>

                                  {/* โปรโมชั่น */}
                                  <td className="px-2 py-1.5">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-center gap-1.5 cursor-default">
                                          <Gift className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                                          <div className="flex flex-col">
                                            <span className="text-gray-900 dark:text-gray-100 font-medium truncate max-w-[120px]">
                                              {title || promoCode}
                                            </span>
                                            <Badge
                                              variant="outline"
                                              className="text-[10px] px-1 py-0 h-4 w-fit bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200"
                                            >
                                              {promoCode}
                                            </Badge>
                                          </div>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-[250px] p-3">
                                        <div className="space-y-1.5">
                                          <div className="font-medium text-sm">
                                            {title || "โปรโมชั่น"}
                                          </div>
                                          <div className="text-xs text-muted-foreground">
                                            รหัส: {promoCode}
                                          </div>
                                          {notif.message_obj?.description && (
                                            <div className="text-xs border-t pt-1.5 mt-1.5">
                                              {notif.message_obj.description}
                                            </div>
                                          )}
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  </td>

                                  {/* จัดการ */}
                                  <td className="px-2 py-1.5 text-center">
                                    {memberAccepted === 0 ? (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                            onClick={() =>
                                              confirmDeleteNotification(
                                                notif.objectId,
                                                promoCode
                                              )
                                            }
                                            disabled={
                                              isDeletingNotification ===
                                              notif.objectId
                                            }
                                          >
                                            {isDeletingNotification ===
                                            notif.objectId ? (
                                              <Loader2 className="w-3 h-3 animate-spin" />
                                            ) : (
                                              <Trash2 className="w-3 h-3" />
                                            )}
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          ยกเลิกโปร
                                        </TooltipContent>
                                      </Tooltip>
                                    ) : memberAccepted === 1 ? (
                                      <span className="text-emerald-500">
                                        ✓
                                      </span>
                                    ) : memberAccepted === 2 ? (
                                      <span className="text-red-500">✗</span>
                                    ) : (
                                      <span className="text-muted-foreground">
                                        -
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {/* Show more hint */}
                  {memberNotifications.length > promoHistoryLimit && (
                    <div className="text-center py-2 text-xs text-muted-foreground border-t">
                      แสดง {promoHistoryLimit} จาก {memberNotifications.length}{" "}
                      รายการ
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ============================================ */}
            {/* TAB 4: ธุรกรรม (Transactions) */}
            {/* ============================================ */}
            <TabsContent
              value="transactions"
              className="m-0 space-y-3 flex-1 overflow-hidden flex flex-col"
            >
              {/* Header - เรียบง่าย */}
              <div className="flex items-center justify-between flex-shrink-0 pb-2 border-b">
                <div className="flex items-center gap-3">
                  <h3 className="text-base font-semibold flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    รายการธุรกรรม
                  </h3>
                  <span className="text-sm text-muted-foreground">
                    ({txTotal} รายการ)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (member) {
                        loadMemberTransactions(member.id, txPage, txLimit);
                        loadMemberAuditLogs(member.id, 1, 20); // โหลด audit logs ด้วย
                      }
                    }}
                    disabled={isLoadingTx}
                    className="h-8"
                  >
                    {isLoadingTx ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                  <Select
                    value={String(txLimit)}
                    onValueChange={(v) => {
                      const newLimit = Number(v);
                      setTxLimit(newLimit);
                      if (member)
                        loadMemberTransactions(member.id, 1, newLimit);
                    }}
                  >
                    <SelectTrigger className="w-[80px] h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* TransactionTable Component */}
              <div className="flex-1 overflow-hidden border rounded-lg bg-card min-h-0">
                <TransactionTable
                  transactions={transactions}
                  isLoading={isLoadingTx}
                  activeFilterType=""
                  hideColumns={["no", "detail", "member"]}
                  compactLayout={true} // ✅ ใช้ compact layout สำหรับ Member Detail Dialog
                  onViewDetail={handleViewTransactionDetail}
                  onViewMember={handleViewTransactionMember}
                  onApproveManual={handleApproveManual}
                  onApproveAuto={handleApproveAuto}
                  onResend={handleResend}
                  onReject={handleReject}
                  onCancel={handleCancel}
                  onComplete={handleComplete}
                  onActionClick={handleActionClick}
                />
              </div>

              {/* Pagination - เรียบง่าย */}
              {txTotal > txLimit && (
                <div className="flex items-center justify-center gap-3 pt-2 flex-shrink-0 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      member &&
                      loadMemberTransactions(member.id, txPage - 1, txLimit)
                    }
                    disabled={txPage <= 1 || isLoadingTx}
                    className="h-8"
                  >
                    ก่อนหน้า
                  </Button>
                  <span className="text-sm text-muted-foreground min-w-[80px] text-center">
                    หน้า {txPage} / {Math.ceil(txTotal / txLimit)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      member &&
                      loadMemberTransactions(member.id, txPage + 1, txLimit)
                    }
                    disabled={
                      txPage >= Math.ceil(txTotal / txLimit) || isLoadingTx
                    }
                    className="h-8"
                  >
                    ถัดไป
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* ============================================ */}
            {/* TAB 5: แนะนำเพื่อน (Referral) - Commission V2 */}
            {/* ============================================ */}
            <TabsContent value="referral" className="m-0 space-y-4">
              {/* Summary Cards - 4 cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {/* ยอดคงเหลือ (ถอนได้) */}
                <Card className="bg-gradient-to-br from-sky-50 to-sky-100/50 dark:from-sky-950/30 dark:to-sky-900/20 border-sky-200">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">ยอดคงเหลือ</p>
                        <p className="text-xl font-bold text-sky-600">
                          {formatCurrency(memberRefer?.totalAmount || 0)}
                        </p>
                      </div>
                      <Wallet className="w-8 h-8 text-sky-500/50" />
                    </div>
                  </CardContent>
                </Card>

                {/* รายได้ทั้งหมด */}
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-950/30 dark:to-purple-900/20 border-purple-200">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">รายได้ทั้งหมด</p>
                        <p className="text-xl font-bold text-purple-600">
                          {formatCurrency(memberRefer?.totalEarned || 0)}
                        </p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-purple-500/50" />
                    </div>
                  </CardContent>
                </Card>

                {/* ถอนแล้วรวม */}
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-950/30 dark:to-emerald-900/20 border-emerald-200">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">ถอนแล้ว</p>
                        <p className="text-xl font-bold text-emerald-600">
                          {formatCurrency(memberRefer?.totalWithdrawn || 0)}
                        </p>
                      </div>
                      <PiggyBank className="w-8 h-8 text-emerald-500/50" />
                    </div>
                  </CardContent>
                </Card>

                {/* จำนวนเพื่อนรวม */}
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20 border-blue-200">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">จำนวนเพื่อน</p>
                        <p className="text-xl font-bold text-blue-600">
                          {(memberRefer?.level1Count || 0) + (memberRefer?.level2Count || 0)}
                        </p>
                      </div>
                      <Users className="w-8 h-8 text-blue-500/50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Withdraw Section */}
              <Card className="border-primary/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Banknote className="w-5 h-5 text-primary" />
                      ถอน Commission
                    </span>
                    <Badge variant={memberRefer?.canWithdraw ? "default" : "secondary"}>
                      {memberRefer?.canWithdraw ? "ถอนได้" : `ถอนได้หลัง ${memberRefer?.withdrawAvailableFrom || "06:00"}`}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-3 items-start">
                    <div className="flex-1">
                      <div className="p-3 rounded-lg bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                        <div className="text-sm text-muted-foreground">ยอดที่ถอนได้ (ถอนทั้งหมด)</div>
                        <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(memberRefer?.totalAmount || 0)} บาท
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          ขั้นต่ำ {memberRefer?.withdrawMin || 100} บาท
                          {memberRefer?.lastCalculatedAt && (
                            <span className="ml-2">
                              | อัพเดท: {new Date(memberRefer.lastCalculatedAt).toLocaleString("th-TH")}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => setShowWithdrawDialog(true)}
                      disabled={!memberRefer?.canWithdraw || (memberRefer?.totalAmount || 0) < (memberRefer?.withdrawMin || 100)}
                      className="gap-2"
                      size="lg"
                    >
                      <ArrowUpCircle className="w-4 h-4" />
                      ถอนทั้งหมด
                    </Button>
                  </div>
                  {!memberRefer?.canWithdraw && memberRefer?.withdrawTimeMessage && (
                    <div className="mt-2 p-2 rounded bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {memberRefer.withdrawTimeMessage}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Commission Details Table */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    รายละเอียด Commission
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20">
                          <th className="px-4 py-3 text-left font-semibold text-muted-foreground">ชั้น</th>
                          <th className="px-4 py-3 text-center font-semibold text-muted-foreground">จำนวนเพื่อน</th>
                          <th className="px-4 py-3 text-right font-semibold text-muted-foreground">Commission</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {/* Level 1 */}
                        <tr className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                              <span className="font-medium">ชั้นที่ 1 (แนะนำตรง)</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50">
                              {memberRefer?.level1Count || 0} คน
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-emerald-600">
                            {formatCurrency(memberRefer?.level1Income || 0)}
                          </td>
                        </tr>

                        {/* Level 2 */}
                        <tr className="hover:bg-muted/30 transition-colors bg-muted/10">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              <span className="font-medium">ชั้นที่ 2 (ลูกของลูก)</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/50">
                              {memberRefer?.level2Count || 0} คน
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-blue-600">
                            {formatCurrency(memberRefer?.level2Income || 0)}
                          </td>
                        </tr>

                        {/* Total Row */}
                        <tr className="bg-gradient-to-r from-sky-50 to-sky-100 dark:from-sky-950/30 dark:to-sky-900/20 font-semibold">
                          <td className="px-4 py-3">
                            <span className="font-bold">รวมทั้งหมด</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className="bg-sky-200 text-sky-800 dark:bg-sky-900">
                              {(memberRefer?.level1Count || 0) + (memberRefer?.level2Count || 0)} คน
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-sky-600">
                            {formatCurrency((memberRefer?.level1Income || 0) + (memberRefer?.level2Income || 0))}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Referral List (ถ้ามี) */}
              {memberRefer?.childList && memberRefer.childList.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-primary" />
                      รายชื่อเพื่อนที่แนะนำ
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-background">
                          <tr className="border-b">
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Username</th>
                            <th className="px-3 py-2 text-center font-medium text-muted-foreground">ชั้น</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {memberRefer.childList.map((child, idx) => (
                            <tr key={child.id || idx} className="hover:bg-muted/30">
                              <td className="px-3 py-2">
                                <span className="font-medium">{child.username}</span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <Badge variant={child.level === 1 ? "default" : "secondary"} className="text-xs">
                                  ชั้น {child.level}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Withdraw History */}
              {memberRefer?.withdrawHistory && memberRefer.withdrawHistory.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <History className="w-5 h-5 text-primary" />
                      ประวัติการถอน
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-background">
                          <tr className="border-b">
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground">วันที่</th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground">จำนวน</th>
                            <th className="px-3 py-2 text-center font-medium text-muted-foreground">สถานะ</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {memberRefer.withdrawHistory.map((item, idx) => (
                            <tr key={item.id || idx} className="hover:bg-muted/30">
                              <td className="px-3 py-2 text-muted-foreground">
                                {item.created_at ? new Date(item.created_at).toLocaleString("th-TH", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit"
                                }) : "-"}
                              </td>
                              <td className="px-3 py-2 text-right font-medium">
                                {formatCurrency(item.withdraw_amount)}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <Badge 
                                  variant={item.status === "completed" ? "default" : item.status === "pending" ? "secondary" : "destructive"}
                                  className="text-xs"
                                >
                                  {item.status === "completed" ? "สำเร็จ" : 
                                   item.status === "pending" ? "รอดำเนินการ" :
                                   item.status === "approved" ? "อนุมัติ" : "ยกเลิก"}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Info Box & Refresh */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 rounded-lg border bg-muted/30 p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p><strong>ชั้นที่ 1:</strong> เพื่อนที่สมาชิกแนะนำโดยตรง</p>
                      <p><strong>ชั้นที่ 2:</strong> เพื่อนที่เพื่อนชั้นที่ 1 แนะนำ (ลูกของลูก)</p>
                      <p className="text-xs pt-1">* Commission คำนวณทุกวัน ยอดถอนได้หลังเวลา {memberRefer?.withdrawAvailableFrom || "06:00"} น.</p>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => member && loadMemberReferral(member.id)}
                  className="gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  รีเฟรช
                </Button>
              </div>
            </TabsContent>

            {/* Withdraw Confirmation Dialog */}
            <AlertDialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>ยืนยันการถอน Commission</AlertDialogTitle>
                  <AlertDialogDescription>
                    <div className="space-y-3 mt-2">
                      <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                        <div className="text-sm text-muted-foreground">จำนวนที่จะถอน (ทั้งหมด)</div>
                        <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(memberRefer?.totalAmount || 0)} บาท
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        หลังถอนสำเร็จ ยอดจะ reset เป็น 0 และเริ่มสะสมใหม่ในรอบถัดไป
                      </p>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isWithdrawing}>ยกเลิก</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleWithdrawCommission}
                    disabled={isWithdrawing}
                    className="gap-2"
                  >
                    {isWithdrawing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        กำลังดำเนินการ...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        ยืนยันถอน
                      </>
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* ============================================ */}
            {/* TAB 6: กิจกรรม (Audit Logs) - Compact List */}
            {/* ============================================ */}
            <TabsContent value="audit" className="m-0 space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  ประวัติกิจกรรม ({auditTotal} รายการ)
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    member && loadMemberAuditLogs(member.id, 1, auditLimit)
                  }
                  disabled={isLoadingAudit}
                >
                  {isLoadingAudit ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-1" />
                  )}
                  รีเฟรช
                </Button>
              </div>

              {/* Compact Audit Log List */}
              <div className="border rounded-lg overflow-hidden">
                {isLoadingAudit ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : auditLogs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <div>ไม่พบประวัติกิจกรรม</div>
                  </div>
                ) : (
                  <div className="divide-y">
                    {auditLogs.map((log) => {
                      const timestamp =
                        typeof log.timestamp === "string"
                          ? new Date(log.timestamp)
                          : log.timestamp &&
                            typeof log.timestamp === "object" &&
                            "iso" in log.timestamp
                          ? new Date(log.timestamp.iso)
                          : new Date();

                      const isExpanded = expandedLogId === log.id;
                      const hasChanges = log.changes && log.changes.length > 0;

                      return (
                        <div key={log.id}>
                          {/* Compact Row - กดเพื่อ expand */}
                          <button
                            className={cn(
                              "w-full text-left px-3 py-2 hover:bg-muted/30 transition-colors flex items-center gap-3",
                              isExpanded && "bg-muted/50"
                            )}
                            onClick={() =>
                              setExpandedLogId(isExpanded ? null : log.id)
                            }
                          >
                            {/* Action Badge */}
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-xs flex-shrink-0",
                                log.action === "create" ||
                                  log.action === "approve"
                                  ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                                  : log.action === "delete" ||
                                    log.action === "reject"
                                  ? "border-red-300 text-red-700 bg-red-50"
                                  : log.action === "update"
                                  ? "border-blue-300 text-blue-700 bg-blue-50"
                                  : ""
                              )}
                            >
                              {AuditActionLabels[log.action] || log.action}
                            </Badge>

                            {/* Description (truncated) */}
                            <span className="flex-1 text-sm truncate">
                              {log.description}
                            </span>

                            {/* Actor & Time */}
                            <span className="text-xs text-muted-foreground flex-shrink-0 hidden sm:block">
                              {log.actorName}
                            </span>
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                              {timestamp.toLocaleDateString("th-TH", {
                                day: "2-digit",
                                month: "short",
                              })}
                            </span>

                            {/* Expand indicator */}
                            {hasChanges && (
                              <Eye
                                className={cn(
                                  "w-4 h-4 text-muted-foreground transition-transform",
                                  isExpanded && "rotate-180"
                                )}
                              />
                            )}
                          </button>

                          {/* Expanded Details */}
                          {isExpanded && (
                            <div className="px-4 py-3 bg-muted/20 border-t space-y-2">
                              {/* Full Info */}
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-muted-foreground">
                                    ทำโดย:
                                  </span>
                                  <span className="ml-1 font-medium">
                                    {log.actorName} ({log.actorUsername})
                                  </span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">
                                    เวลา:
                                  </span>
                                  <span className="ml-1">
                                    {timestamp.toLocaleString("th-TH")}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-muted-foreground">
                                    รายละเอียด:
                                  </span>
                                  <span className="ml-1">
                                    {log.description}
                                  </span>
                                </div>
                              </div>

                              {/* Changes */}
                              {hasChanges && (
                                <div className="bg-background rounded p-2 space-y-1">
                                  <div className="text-xs font-medium text-muted-foreground mb-1">
                                    ข้อมูลที่เปลี่ยน:
                                  </div>
                                  {log.changes?.map((change, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center gap-2 text-xs"
                                    >
                                      <span className="text-muted-foreground w-20">
                                        {change.fieldLabel || change.field}:
                                      </span>
                                      <span className="line-through text-red-500">
                                        {String(change.oldValue || "-")}
                                      </span>
                                      <span className="text-muted-foreground">
                                        →
                                      </span>
                                      <span className="text-emerald-600 font-medium">
                                        {String(change.newValue || "-")}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Status */}
                              {log.status === "failed" && (
                                <div className="text-xs text-red-600 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  ล้มเหลว: {log.errorMessage}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {auditTotal > auditLimit && (
                <div className="flex items-center justify-center gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      member &&
                      loadMemberAuditLogs(member.id, auditPage - 1, auditLimit)
                    }
                    disabled={auditPage <= 1 || isLoadingAudit}
                  >
                    ก่อนหน้า
                  </Button>
                  <span className="text-sm px-3">
                    หน้า {auditPage} / {Math.ceil(auditTotal / auditLimit)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      member &&
                      loadMemberAuditLogs(member.id, auditPage + 1, auditLimit)
                    }
                    disabled={
                      auditPage >= Math.ceil(auditTotal / auditLimit) ||
                      isLoadingAudit
                    }
                  >
                    ถัดไป
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* ============================================ */}
            {/* TAB 7: ประวัติเกม (Bet History) */}
            {/* ============================================ */}
            <TabsContent value="bethistory" className="m-0">
              {member && (
                <MemberBetHistoryTab memberId={member.id} />
              )}
            </TabsContent>

            {/* ============================================ */}
            {/* TAB 8: แก้ไขข้อมูล */}
            {/* ============================================ */}
            <TabsContent value="edit" className="m-0 space-y-4">
              {/* Personal Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-500" />
                    ข้อมูลส่วนตัว
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ชื่อ</Label>
                    <Input
                      value={formData.firstName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          firstName: sanitizeName(e.target.value),
                        })
                      }
                      placeholder="ชื่อ"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>นามสกุล</Label>
                    <Input
                      value={formData.lastName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          lastName: sanitizeName(e.target.value),
                        })
                      }
                      placeholder="นามสกุล"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>เบอร์โทรศัพท์ (Username)</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            phone: sanitizePhone(e.target.value),
                          })
                        }
                        className="pl-9"
                        placeholder="0812345678"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bank Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-emerald-500" />
                    ข้อมูลธนาคาร
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ธนาคาร</Label>
                    <Select
                      value={formData.bankName}
                      onValueChange={(value) =>
                        setFormData({ ...formData, bankName: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="เลือกธนาคาร">
                          {formData.bankName && (
                            <div className="flex items-center gap-2">
                              <BankLogo
                                bankValue={formData.bankName}
                                size="xs"
                              />
                              <span>{findBank(formData.bankName)?.name}</span>
                            </div>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {BANK_LIST.map((bank) => (
                          <SelectItem key={bank.id} value={bank.id}>
                            <div className="flex items-center gap-2">
                              <BankLogo bankValue={bank.id} size="xs" />
                              <span>{bank.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>เลขบัญชี</Label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={formData.bankAccount}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            bankAccount: sanitizeBankAccount(e.target.value),
                          })
                        }
                        className="pl-9"
                        placeholder="1234567890"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Security */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Shield className="w-4 h-4 text-orange-500" />
                    ความปลอดภัย
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>รหัสผ่านใหม่</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                        placeholder="ปล่อยว่างถ้าไม่ต้องการเปลี่ยน"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-orange-600">
                      ***
                      ใส่รหัสผ่านใหม่แล้วกดบันทึกเพื่อเปลี่ยนรหัสผ่านให้ลูกค้า
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Agent Username
                      </Label>
                      <Input
                        value={member?.agentUsername || "-"}
                        disabled
                        className="bg-muted font-mono"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Agent Password
                      </Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={member?.agentPassword || "-"}
                          disabled
                          className="bg-muted font-mono pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end pt-2">
                <Button onClick={handleSaveClick} disabled={isSaving} size="lg">
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  บันทึกข้อมูล
                </Button>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>

      {/* Confirm Save Dialog */}
      <SaveConfirmDialog
        open={showConfirmSave}
        onOpenChange={setShowConfirmSave}
        onConfirm={handleConfirmSave}
        isLoading={isSaving}
        itemName="ข้อมูลสมาชิก"
        changes={getChanges()}
      />

      {/* 🔐 Action Confirmation Dialog */}
      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) => !open && closeConfirmDialog()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle
              className={cn(
                confirmDialog.variant === "danger" && "text-red-600"
              )}
            >
              {confirmDialog.title}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              className={cn(
                confirmDialog.variant === "danger" &&
                  "bg-red-600 hover:bg-red-700"
              )}
            >
              ยืนยัน
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 🎁 Promotion Confirmation Dialog */}
      <AlertDialog
        open={promoConfirmDialog.open}
        onOpenChange={(open) =>
          !open && setPromoConfirmDialog({ ...promoConfirmDialog, open: false })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle
              className={cn(
                promoConfirmDialog.type === "delete" && "text-red-600"
              )}
            >
              {promoConfirmDialog.type === "send"
                ? "ยืนยันการส่งโปรโมชั่น"
                : "ยืนยันการยกเลิกโปรโมชั่น"}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {promoConfirmDialog.type === "send" ? (
                <>
                  <p>คุณต้องการส่งโปรโมชั่นให้สมาชิกนี้ใช่หรือไม่?</p>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <Gift className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium text-emerald-700 dark:text-emerald-400">
                      {promoConfirmDialog.promoName}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <p>คุณต้องการยกเลิกโปรโมชั่นนี้ใช่หรือไม่?</p>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <Gift className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-red-700 dark:text-red-400">
                      {promoConfirmDialog.promoName}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    การยกเลิกจะทำให้สมาชิกไม่สามารถรับโปรโมชั่นนี้ได้อีก
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePromoConfirm}
              className={cn(
                promoConfirmDialog.type === "send"
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-red-600 hover:bg-red-700"
              )}
            >
              {promoConfirmDialog.type === "send"
                ? "ยืนยันส่งโปร"
                : "ยืนยันยกเลิก"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transaction Dialog - สำหรับดูรายละเอียด transaction (view-only) */}
      <TransactionDialog
        open={transactionDialogOpen}
        onOpenChange={setTransactionDialogOpen}
        mode="view" // ✅ เปิดเป็น view-only เสมอเมื่ออยู่ใน member detail dialog
        transaction={selectedTransaction || undefined}
        onSave={async () => {
          // Reload transactions after save (ไม่ควรเรียกใช้เพราะเป็น view-only)
          if (member) {
            await loadMemberTransactions(member.id, txPage, txLimit);
          }
          setTransactionDialogOpen(false);
        }}
      />
    </Dialog>
  );
}
